import React from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { CustomDatePicker } from '../../ui/custom-date-picker';
import { FieldError } from '../StatusBadge';
import { Lock } from 'lucide-react';

// Default values that should be treated as "empty" for border styling
const DEFAULT_VALUES = {
  bill_status: 'pending'
};

// Helper function to check if a dropdown has a non-default value (for black border)
const hasSelectedValue = (field, value) => {
  if (!value) return false;
  const defaultVal = DEFAULT_VALUES[field];
  if (defaultVal !== undefined) {
    return String(value) !== String(defaultVal);
  }
  return true;
};

function SingleBillingBlock({ prefix, label, formData, handleFieldChange, errors, compact, inputH }) {
  const statusField = prefix ? `bill_status_${prefix}` : 'bill_status';
  const dateField = prefix ? `bill_payment_date_${prefix}` : 'bill_payment_date';
  const statusValue = formData[statusField] || 'pending';
  const dateValue = formData[dateField] || '';

  return (
    <div className="space-y-2">
      {label && <div className="text-xs font-bold text-slate-600 uppercase tracking-wide border-b border-slate-100 pb-1">{label}</div>}
      <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">Bill Status</Label>
          <Select value={statusValue} onValueChange={(v) => handleFieldChange(statusField, v)}>
            <SelectTrigger data-testid={`billing-${statusField}`} className={`${inputH} ${hasSelectedValue('bill_status', statusValue) ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500 ml-1">Payment Date {statusValue === 'paid' ? '*' : ''}</Label>
          <CustomDatePicker 
            data-testid={`billing-${dateField}`}
            value={dateValue} 
            onChange={(date) => handleFieldChange(dateField, date || '')} 
            placeholder="Select date"
            min={formData.date_received || undefined}
            allowFuture={false}
          />
          <FieldError error={errors[dateField]} />
        </div>
      </div>
    </div>
  );
}

export function BillingContent({ formData, handleFieldChange, errors, preventWheelChange, compact = false, locked = false }) {
  const inputH = 'h-10 rounded-xl';
  const lockedStyle = 'bg-slate-100 cursor-not-allowed opacity-70';
  
  const isSVEDual = formData.client_name === 'SVE CASTINGS PVT LTD' && Number(formData.invoice_count) === 2;
  
  if (locked) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
          <Lock className="w-4 h-4" />
          <span>This section is locked</span>
        </div>
        {isSVEDual ? (
          <div className="space-y-3">
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
              <div className="text-xs font-bold text-blue-800 mb-2">Invoice 1 Billing</div>
              <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500 ml-1">Bill Status</Label>
                  <Input value={(formData.bill_status_1 || 'pending').charAt(0).toUpperCase() + (formData.bill_status_1 || 'pending').slice(1)} readOnly className={`${inputH} ${lockedStyle}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500 ml-1">Payment Date</Label>
                  <Input value={formData.bill_payment_date_1 || ''} readOnly className={`${inputH} ${lockedStyle}`} />
                </div>
              </div>
            </div>
            <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
              <div className="text-xs font-bold text-purple-800 mb-2">Invoice 2 Billing</div>
              <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500 ml-1">Bill Status</Label>
                  <Input value={(formData.bill_status_2 || 'pending').charAt(0).toUpperCase() + (formData.bill_status_2 || 'pending').slice(1)} readOnly className={`${inputH} ${lockedStyle}`} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500 ml-1">Payment Date</Label>
                  <Input value={formData.bill_payment_date_2 || ''} readOnly className={`${inputH} ${lockedStyle}`} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-3 gap-3'}`}>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 ml-1">Bill Status</Label>
              <Input value={formData.bill_status === 'paid' ? 'Paid' : 'Pending'} readOnly className={`${inputH} ${lockedStyle}`} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 ml-1">Payment Date</Label>
              <Input value={formData.bill_payment_date || ''} readOnly className={`${inputH} ${lockedStyle}`} />
            </div>
          </div>
        )}
        <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
          <Input value={formData.follow_up_email || ''} readOnly placeholder="Follow-up Email" className={`${inputH} ${lockedStyle}`} />
          <Input value={formData.follow_up_phone || ''} readOnly placeholder="Follow-up Phone" className={`${inputH} ${lockedStyle}`} />
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {isSVEDual ? (
        /* SVE Dual Invoice: Split billing for each invoice */
        <div className="space-y-3">
          <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
            <SingleBillingBlock
              prefix="1"
              label={`Invoice 1 Billing${formData.generated_invoice_number_1 ? ` (${formData.generated_invoice_number_1})` : ''}`}
              formData={formData}
              handleFieldChange={handleFieldChange}
              errors={errors}
              compact={compact}
              inputH={inputH}
            />
          </div>
          <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
            <SingleBillingBlock
              prefix="2"
              label={`Invoice 2 Billing${formData.generated_invoice_number_2 ? ` (${formData.generated_invoice_number_2})` : ''}`}
              formData={formData}
              handleFieldChange={handleFieldChange}
              errors={errors}
              compact={compact}
              inputH={inputH}
            />
          </div>
          {/* Overall status indicator */}
          {(formData.bill_status_1 === 'paid' || formData.bill_status_2 === 'paid') && !(formData.bill_status_1 === 'paid' && formData.bill_status_2 === 'paid') && (
            <div className="text-xs font-semibold text-amber-600 bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
              Overall Status: Partially Paid — {formData.bill_status_1 === 'paid' ? 'Invoice 1 paid' : 'Invoice 2 paid'}, {formData.bill_status_1 !== 'paid' ? 'Invoice 1 pending' : 'Invoice 2 pending'}
            </div>
          )}
        </div>
      ) : (
        /* Standard single billing */
        <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Bill Status</Label>
            <Select value={formData.bill_status} onValueChange={(v) => handleFieldChange('bill_status', v)}>
              <SelectTrigger data-testid="billing-bill_status" className={`${inputH} ${hasSelectedValue('bill_status', formData.bill_status) ? 'border-slate-900' : ''}`}><SelectValue placeholder="Select Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Payment Date {formData.bill_status === 'paid' ? '*' : ''}</Label>
            <CustomDatePicker 
              data-testid="billing-bill_payment_date"
              value={formData.bill_payment_date} 
              onChange={(date) => handleFieldChange('bill_payment_date', date || '')} 
              placeholder="Select date"
              min={formData.date_received || undefined}
              allowFuture={false}
            />
            <FieldError error={errors.bill_payment_date} />
          </div>
        </div>
      )}
      <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
        <Input value={formData.follow_up_email} onChange={(e) => handleFieldChange('follow_up_email', e.target.value)} placeholder="Follow-up Email" className={`${inputH} ${errors.follow_up_email ? 'border-red-500' : formData.follow_up_email ? 'border-slate-900' : ''}`} />
        <Input value={formData.follow_up_phone} onChange={(e) => handleFieldChange('follow_up_phone', e.target.value)} placeholder="Follow-up Phone" inputMode="numeric" pattern="[0-9]*" className={`${inputH} ${errors.follow_up_phone ? 'border-red-500' : formData.follow_up_phone ? 'border-slate-900' : ''}`} />
      </div>
    </div>
  );
}
